import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Check if Supabase is properly configured
const isSupabaseConfigured = supabaseUrl && 
  supabaseAnonKey && 
  supabaseUrl !== 'https://placeholder.supabase.co' && 
  supabaseAnonKey !== 'placeholder-key-replace-with-actual-key' &&
  supabaseUrl.startsWith('https://') &&
  supabaseUrl.includes('.supabase.co');

if (!isSupabaseConfigured) {
  console.warn('Supabase not configured or using placeholder values. Supabase features will be disabled.');
}

// Create Supabase client with fallback for invalid configurations
export const supabase = isSupabaseConfigured ? 
  createClient(supabaseUrl, supabaseAnonKey) : 
  null;

// Helper function to save abandoned checkout
export const saveAbandonedCheckout = async (checkoutData, status = 'abandoned') => {
  if (!supabase) {
    console.warn('Supabase not configured - skipping abandoned checkout save');
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    const { data, error } = await supabase
      .from('abandoned_checkouts')
      .upsert({
        email: checkoutData.email,
        cart_data: checkoutData.cart,
        shipping_info: checkoutData.shipping,
        total: checkoutData.total,
        status: status,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'email',
        ignoreDuplicates: false,
      });

    if (error) {
      console.error('Error saving checkout data:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Exception in saveAbandonedCheckout:', error);
    return { success: false, error };
  }
};

// Helper function to retrieve abandoned checkout by email
export const getAbandonedCheckout = async (email) => {
  if (!supabase) {
    console.warn('Supabase not configured - skipping abandoned checkout retrieval');
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    const { data, error } = await supabase
      .from('abandoned_checkouts')
      .select('*')
      .eq('email', email)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No abandoned checkout found for this email
        return { success: true, data: null };
      }
      console.error('Error retrieving abandoned checkout:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Exception in getAbandonedCheckout:', error);
    return { success: false, error };
  }
};

// Function to update checkout status
export const updateCheckoutStatus = async (email, status) => {
  if (!supabase) {
    console.warn('Supabase not configured - skipping checkout status update');
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    const { data, error } = await supabase
      .from('abandoned_checkouts')
      .update({ 
        status: status,
        updated_at: new Date().toISOString()
      })
      .eq('email', email);

    if (error) {
      console.error('Error updating checkout status:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Exception in updateCheckoutStatus:', error);
    return { success: false, error };
  }
};

// Function to mark checkout as completed
export const completeCheckout = async (email) => {
  return await updateCheckoutStatus(email, 'completed');
};

// Function to track checkout analytics
export const trackCheckoutEvent = async (email, event, metadata = {}) => {
  if (!supabase) {
    console.warn('Supabase not configured - skipping event tracking');
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    const { data, error } = await supabase
      .from('checkout_events')
      .insert({
        email: email,
        event_type: event,
        metadata: metadata,
        created_at: new Date().toISOString()
      });

    if (error) {
      console.error('Error tracking checkout event:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Exception in trackCheckoutEvent:', error);
    return { success: false, error };
  }
};

// Function to check for payment gateway callbacks and update checkout status
export const checkPaymentCallback = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const paymentStatus = queryParams.get('payment_status') || queryParams.get('status');
  const email = queryParams.get('buyer_email') || queryParams.get('email') || localStorage.getItem('checkout_email');
  
  if (paymentStatus && email) {
    console.log(`Payment callback detected: ${paymentStatus} for ${email}`);
    
    // Handle both Gumroad and Kit.com status formats
    if (paymentStatus === 'success' || paymentStatus === 'paid' || paymentStatus === 'completed') {
      // Payment was successful
      completeCheckout(email);
      trackCheckoutEvent(email, 'payment_completed', {
        payment_status: paymentStatus,
        callback_url: window.location.href,
        payment_provider: 'kit.com'
      });
      
      // Clear cart data since payment is completed
      localStorage.removeItem('checkout_cart_data');
      return { success: true, status: 'completed' };
    } else if (paymentStatus === 'failed' || paymentStatus === 'cancelled' || paymentStatus === 'canceled') {
      // Payment failed or cancelled
      updateCheckoutStatus(email, 'abandoned');
      trackCheckoutEvent(email, 'payment_failed', {
        payment_status: paymentStatus,
        callback_url: window.location.href,
        payment_provider: 'kit.com'
      });
      return { success: true, status: 'failed' };
    }
  }
  
  return { success: false, status: null };
}; 